import { z } from "zod";

/**
 * Every language the platform is ready to speak one day. Adding one to the apps is moving it into
 * `APP_LANGUAGES`, never typing a new code somewhere. Nothing offers a language from here directly:
 * every picker, schema and translation reads `APP_LANGUAGES`.
 */
export enum Language {
  EN = "en",
  PL = "pl",
  DE = "de",
  ES = "es",
  FR = "fr",
  IT = "it",
  PT = "pt",
  NL = "nl",
  CS = "cs",
  SK = "sk",
  UK = "uk",
  LT = "lt",
}

/**
 * The languages the platform speaks today, and the only ones anything offers: the apps' interface,
 * emails and notifications, and everything people write in several languages (exercises, blog
 * posts, broadcasts). English is first and the default, at plain addresses (ADR 0026). Each app's
 * `project.inlang` lists exactly these, which its tests check.
 */
export const APP_LANGUAGES = [Language.EN, Language.PL] as const;
export type AppLanguage = (typeof APP_LANGUAGES)[number];

export const DEFAULT_LANGUAGE = Language.EN;

/** The `og:locale` of each language, for link previews. */
export const OG_LOCALES: Record<Language, string> = {
  [Language.EN]: "en_US",
  [Language.PL]: "pl_PL",
  [Language.DE]: "de_DE",
  [Language.ES]: "es_ES",
  [Language.FR]: "fr_FR",
  [Language.IT]: "it_IT",
  [Language.PT]: "pt_PT",
  [Language.NL]: "nl_NL",
  [Language.CS]: "cs_CZ",
  [Language.SK]: "sk_SK",
  [Language.UK]: "uk_UA",
  [Language.LT]: "lt_LT",
};

export const isAppLanguage = (value: unknown): value is AppLanguage =>
  APP_LANGUAGES.some((language) => language === value);

/** The app language a plain code (`"pl"`, from Paraglide or a URL) stands for, if the apps speak it. */
export const appLanguageOf = (code: string): AppLanguage | undefined =>
  APP_LANGUAGES.find((language) => language === code);

/**
 * The header every app's API client sends with the language the page is in, so the API answers in
 * it too: its messages and anything stored in several languages, such as exercise names.
 */
export const APP_LANGUAGE_HEADER = "x-app-language";

/**
 * Text written in other app languages besides the one an item was written in: `{ pl: { ... } }`.
 * A reader gets the one in their language, else the item's own. Built from `APP_LANGUAGES`, so a
 * language added there can be written everywhere at once.
 */
export const appLanguageTranslationsSchema = <Text extends z.ZodType>(text: Text) =>
  z.partialRecord(z.enum(APP_LANGUAGES), text);

/** Something written once per app language, such as an admin broadcast's title and body. */
export type PerAppLanguage<T> = Record<AppLanguage, T>;
